import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { connect } from 'react-redux';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { stateVegnettDirection, stateValgt } from '@/selectors/selectors';
import { PreferencesState, ValgtState, RootState } from '@/state';
import {
    isPolygonFeature,
    roadNetLinkToFeature,
    roadObjectToFeature,
} from '@/utils/SpatialHelper';
import { selectedFeatureStyle } from '../style/Style';
import { StyleLike } from 'ol/style/Style';
import { FeatureLayerType, FeatureLayerTypeKey } from './FeatureLayerType';
import React from 'react';
import { MapContext } from '@/state/context/MapContext';
import { Cluster } from 'ol/source';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { statePreferences } from '@/selectors/selectors';

type SelectedFeature = Feature<Geometry>;
type SelectedSource = VectorSource<SelectedFeature>;
type SelectionLayer = VectorLayer<
    SelectedSource | Cluster<SelectedFeature>,
    SelectedFeature
>;

interface TagProps {
    zIndex: number;
}

interface StateProps {
    selection: ValgtState;
    vegnettRetningsgrunnlag: string;
    preferences: PreferencesState;
}

type Props = TagProps & StateProps;

export class ValgtFeatureLayer extends React.Component<Props, Record<string, never>> {
    static contextType = MapContext;
    declare context: React.ContextType<typeof MapContext>;

    private selectionLayer?: SelectionLayer;

    componentDidMount() {
        this.updateSelectionLayer(this.props.selection);
    }

    componentDidUpdate(prevProps: Props) {
        const selection = this.props.selection;
        if (prevProps.selection !== selection && !selection.fetching) {
            this.updateSelectionLayer(selection);
        }
    }

    componentWillUnmount(): void {
        if (this.selectionLayer) {
            this.context.map.removeLayer(this.selectionLayer);
        }
    }

    updateSelectionLayer(selected: ValgtState) {
        // Remove previous selection layer
        if (this.selectionLayer) {
            this.context.map.removeLayer(this.selectionLayer);
            this.selectionLayer = undefined;
        }

        let features: SelectedFeature[] = [];
        if (selected.isSingle()) {
            // Vegobjekt -> Feature conversion
            const vegobjekt: Vegobjekt | null = selected.singleVegobjekt();
            if (vegobjekt) {
                if (vegobjekt.geometri == null) return;
                features = [
                    roadObjectToFeature(
                        vegobjekt,
                        this.props.preferences.useEgengeometriWhenAvailable &&
                            vegobjekt.geometri.egengeometri
                            ? vegobjekt.geometri.wkt
                            : (vegobjekt.lokasjon?.geometri?.wkt ??
                                  vegobjekt.geometri.wkt),
                        FeatureLayerType.FEATURE_VALGT
                    ),
                ];
            }
            // Vegnettlenke -> Feature conversion
            const selectedVegnettlenke = selected.singleVegnettlenke();
            if (selectedVegnettlenke) {
                const vegnettlenke: Vegnettlenke = selectedVegnettlenke;
                if (vegnettlenke.geometri == null) return;
                features = [
                    roadNetLinkToFeature(
                        vegnettlenke,
                        this.props.vegnettRetningsgrunnlag
                    ),
                ];
            }

            if (features.length === 0) return;

            // Selection source
            const selectedSource = new VectorSource({
                features,
            });

            // Selection layer
            this.selectionLayer = new VectorLayer({
                source: selectedSource,
                style: selectedFeatureStyle as StyleLike,
                visible: true,
                zIndex:
                    selected.isSingle() && isPolygonFeature(features[0])
                        ? 101
                        : this.props.zIndex,
            });

            // Add new selection layer
            this.context.map.addLayer(this.selectionLayer);
        } else if (
            selected.clusterObjekter.filter(v => v.geometri !== null)?.length > 0
        ) {
            // Vegobjekt-cluster -> Features conversion
            features = selected.clusterObjekter
                .filter(
                    (
                        vegobjekt
                    ): vegobjekt is Vegobjekt & {
                        geometri: NonNullable<Vegobjekt['geometri']>;
                    } => vegobjekt.geometri != null
                )
                .map(vegobjekt =>
                    roadObjectToFeature(
                        vegobjekt,
                        vegobjekt.geometri.wkt,
                        FeatureLayerType.FEATURE_VALGT_CLUSTER
                    )
                );

            // Selection source
            const selectedSource = new VectorSource({
                features,
            });

            // Clustering source
            const clusterSource = new Cluster({
                distance: 50,
                source: selectedSource,
            });

            clusterSource.on('addfeature', e => {
                if (e.feature) {
                    ValgtFeatureLayer.tagClusterFeatures(e.feature);
                }
            });

            // Selection layer
            this.selectionLayer = new VectorLayer({
                source: clusterSource,
                style: selectedFeatureStyle as StyleLike,
                visible: true,
                zIndex: this.props.zIndex,
            });

            // Add new selection layer
            this.context.map.addLayer(this.selectionLayer);
        }
    }

    private static tagClusterFeatures(feature: Feature<Geometry>) {
        feature.set(FeatureLayerTypeKey, FeatureLayerType.FEATURE_VALGT_CLUSTER);
    }

    render() {
        return null;
    }
}

const mapStateToProps = (state: RootState): StateProps => ({
    selection: stateValgt(state),
    vegnettRetningsgrunnlag: stateVegnettDirection(state),
    preferences: statePreferences(state),
});

export const ValgtLayer = connect<StateProps, never, TagProps, RootState>(
    mapStateToProps
)(ValgtFeatureLayer);
